import * as Sentry from "@sentry/react";
import { Backend } from "./backend";

interface ApiErrorResponse {
  key?: string;
  message: string;
  params?: Record<string, unknown>;
}

export class ApiError extends Error {
  public readonly key?: string;
  public readonly params?: Record<string, unknown>;
  public readonly status: number;

  constructor(response: ApiErrorResponse, status: number) {
    super(response.message || "An error occurred");
    this.key = response.key;
    this.params = response.params;
    this.status = status;
  }
}

export class ApiBackend implements Backend {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? window.location.origin;
  }

  async get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    const response = await fetch(this.buildUrl(path, query), {
      method: "GET",
      headers: this.defaultHeaders(),
    });
    return handleResponse(response);
  }

  async post<T>(
    path: string,
    data: unknown,
    query?: Record<string, unknown>,
  ): Promise<T> {
    const response = await fetch(this.buildUrl(path, query), {
      method: "POST",
      headers: this.defaultHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  }

  async put<T>(
    path: string,
    data: unknown,
    query?: Record<string, unknown>,
  ): Promise<T> {
    const response = await fetch(this.buildUrl(path, query), {
      method: "PUT",
      headers: this.defaultHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  }

  async patch<T>(
    path: string,
    data: unknown,
    query?: Record<string, unknown>,
  ): Promise<T> {
    const response = await fetch(this.buildUrl(path, query), {
      method: "PATCH",
      headers: this.defaultHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  }

  async delete<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    const response = await fetch(this.buildUrl(path, query), {
      method: "DELETE",
      headers: this.defaultHeaders(),
    });
    return handleResponse(response);
  }

  private buildUrl(path: string, query?: Record<string, unknown>): string {
    const url = new URL(path, this.baseUrl);
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private defaultHeaders(): HeadersInit {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }
}

// 424 is how the backend surfaces an upstream failure (payment provider,
// e-mail delivery); the remaining 4xx are expected outcomes of a public
// booking flow (unknown slug, sold-out unit, validation) and should not
// raise a Sentry event or trigger an error replay.
const HTTP_FAILED_DEPENDENCY = 424;

function isUnexpectedFailure(status: number): boolean {
  return status >= 500 || status === HTTP_FAILED_DEPENDENCY;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return null as T;
    return response.json();
  }

  const err: ApiErrorResponse = await response.json();
  const error = new ApiError(err, response.status);
  if (isUnexpectedFailure(response.status)) {
    Sentry.captureException(error, {
      extra: { url: response.url, status: response.status },
    });
  }
  throw error;
}
